import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  MagnifyingGlassIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { Button } from './ui';
import groupService from '../services/groupService';
import projectService from '../services/projectService';

const MAX_BATCH_GROUPS = 100;
const USERNAME_PATTERN = /^[a-z0-9_]{5,32}$/i;

const STATUS_MESSAGES = {
  not_found: 'Public group not found.',
  private: 'This username is private or is not a public group.',
  banned: 'The available runtime account is banned from this group.',
};

const BATCH_STATUS = {
  processing: { label: 'Processing', classes: 'bg-blue-100 text-blue-800' },
  subscribed: { label: 'Subscribed & added', classes: 'bg-green-100 text-green-800' },
  known_attached: { label: 'Already known · attached', classes: 'bg-gray-100 text-gray-700' },
  duplicate: { label: 'Duplicate input', classes: 'bg-gray-100 text-gray-700' },
  invite_requested: { label: 'Invite requested & added', classes: 'bg-indigo-100 text-indigo-800' },
  invalid: { label: 'Invalid username', classes: 'bg-red-100 text-red-800' },
  not_found: { label: 'Not found', classes: 'bg-red-100 text-red-800' },
  private: { label: 'Private', classes: 'bg-red-100 text-red-800' },
  banned: { label: 'Account banned', classes: 'bg-red-100 text-red-800' },
  rate_limited: { label: 'Rate limited', classes: 'bg-amber-100 text-amber-800' },
  failed: { label: 'Failed', classes: 'bg-red-100 text-red-800' },
};

const normalizeBatchUsername = (value) => {
  const normalized = String(value || '')
    .trim()
    .replace(/^(?:https?:\/\/)?(?:t\.me|telegram\.me)\//i, '')
    .replace(/^@+/, '')
    .split(/[/?#]/)[0]
    .toLowerCase();

  return USERNAME_PATTERN.test(normalized) ? normalized : null;
};

const classifyBatchError = (error) => {
  const code = String(error?.response?.data?.code || error?.code || '').toUpperCase();
  const message = String(
    error?.response?.data?.message || error?.message || 'Failed to subscribe and add the group.'
  );
  const searchable = `${code} ${message}`.toUpperCase();

  if (
    error?.response?.status === 429 ||
    searchable.includes('RATE LIMIT') ||
    searchable.includes('RATE_LIMIT') ||
    searchable.includes('FLOOD_WAIT') ||
    searchable.includes('TOO MANY REQUESTS')
  ) {
    return { status: 'rate_limited', message };
  }
  if (code === 'INVALID_USERNAME' || searchable.includes('USERNAME_INVALID')) {
    return { status: 'invalid', message };
  }
  if (searchable.includes('NOT_FOUND') || searchable.includes('NOT FOUND')) {
    return { status: 'not_found', message };
  }
  if (searchable.includes('PRIVATE') || searchable.includes('ACCESS_DENIED')) {
    return { status: 'private', message };
  }

  return { status: 'failed', message };
};

const PublicGroupSearchCard = ({ onBatchProcessingChange }) => {
  const [username, setUsername] = useState('');
  const [result, setResult] = useState(null);
  const [projectId, setProjectId] = useState('');
  const [message, setMessage] = useState(null);
  const [batchInput, setBatchInput] = useState('');
  const [batchResults, setBatchResults] = useState([]);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const queryClient = useQueryClient();

  const projectsQuery = useQuery(
    'public-group-search-projects',
    () => projectService.getProjects()
  );
  const projects = Array.isArray(projectsQuery.data) ? projectsQuery.data : [];

  const searchMutation = useMutation(
    () => groupService.searchPublicGroup(username),
    {
      onSuccess: (data) => {
        setResult(data);
        setMessage(null);
      },
      onError: (error) => {
        setResult(null);
        setMessage(
          error?.response?.data?.message ||
          error?.message ||
          'Failed to search for the public group.'
        );
      },
    }
  );

  const subscribeMutation = useMutation(
    () => groupService.subscribeAndAddPublicGroup({
      username: result?.group?.username || username,
      project_id: Number(projectId),
    }),
    {
      onSuccess: (data) => {
        if (data.status === 'subscribed' || data.status === 'invite_requested') {
          setResult({ status: 'found', group: data.group });
          setMessage(
            data.status === 'invite_requested'
              ? 'Subscription request sent and group added to the project.'
              : data.duplicate
                ? 'Group was already known; project attachment is up to date.'
                : 'Subscribed and added to the project.'
          );
          queryClient.invalidateQueries('groups');
          queryClient.invalidateQueries('project-chats');
          return;
        }

        setResult(data);
        setMessage(null);
      },
      onError: (error) => {
        setMessage(
          error?.response?.data?.message ||
          error?.message ||
          'Failed to subscribe and add the public group.'
        );
      },
    }
  );

  const handleSearch = (event) => {
    event.preventDefault();
    if (!username.trim()) {
      setMessage('Enter a public group username.');
      return;
    }
    setMessage(null);
    searchMutation.mutate();
  };

  const updateBatchResult = (id, changes) => {
    setBatchResults(current => current.map(row => (
      row.id === id ? { ...row, ...changes } : row
    )));
  };

  const handleBatchSubscribe = async (event) => {
    event.preventDefault();
    const lines = batchInput
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean);

    if (!projectId || lines.length === 0 || lines.length > MAX_BATCH_GROUPS) {
      return;
    }

    const firstByUsername = new Map();
    const rows = lines.map((input, index) => {
      const usernameValue = normalizeBatchUsername(input);
      let duplicateOf = null;
      if (usernameValue) {
        duplicateOf = firstByUsername.get(usernameValue) ?? null;
        if (duplicateOf === null) {
          firstByUsername.set(usernameValue, index);
        }
      }
      return {
        id: index,
        input,
        username: usernameValue,
        duplicateOf,
        status: usernameValue ? (duplicateOf === null ? 'processing' : 'duplicate') : 'invalid',
        message: usernameValue
          ? (duplicateOf === null ? 'Waiting to be processed.' : `Same as line ${duplicateOf + 1}.`)
          : 'Use a public @username or t.me link.',
      };
    });

    setBatchResults(rows);
    setIsBatchProcessing(true);
    onBatchProcessingChange?.(true);

    try {
      for (const row of rows) {
        if (!row.username || row.duplicateOf !== null) {
          continue;
        }

        updateBatchResult(row.id, { status: 'processing', message: 'Subscribing and attaching...' });
        try {
          const data = await groupService.subscribeAndAddPublicGroup({
            username: row.username,
            project_id: Number(projectId),
          });

          if (data.status === 'subscribed') {
            updateBatchResult(row.id, {
              status: data.duplicate ? 'known_attached' : 'subscribed',
              message: data.duplicate
                ? 'The group was already known; the project attachment is up to date.'
                : 'Subscribed and attached to the selected project.',
            });
          } else if (data.status === 'invite_requested') {
            updateBatchResult(row.id, {
              status: 'invite_requested',
              message: 'The join request was sent and the group was attached to the project.',
            });
          } else {
            updateBatchResult(row.id, {
              status: BATCH_STATUS[data.status] ? data.status : 'failed',
              message: STATUS_MESSAGES[data.status] || 'Telegram could not subscribe to this group.',
            });
          }
        } catch (error) {
          updateBatchResult(row.id, classifyBatchError(error));
        }
      }
    } finally {
      setIsBatchProcessing(false);
      onBatchProcessingChange?.(false);
    }

    queryClient.invalidateQueries('groups');
    queryClient.invalidateQueries('project-chats');
  };

  const group = result?.status === 'found' ? result.group : null;
  const statusMessage = result && result.status !== 'found'
    ? STATUS_MESSAGES[result.status]
    : null;

  return (
    <section className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-gray-900">
          Find a public group
        </h4>
        <p className="mt-1 text-xs text-gray-600">
          Preview a public Telegram group, subscribe with a free production runtime account,
          and attach it to a project.
        </p>
      </div>

      <form onSubmit={handleSearch} className="flex flex-col gap-2 sm:flex-row">
        <label htmlFor="public-group-username" className="sr-only">
          Public group username
        </label>
        <input
          id="public-group-username"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
          placeholder="@public_group"
          disabled={searchMutation.isLoading || subscribeMutation.isLoading || isBatchProcessing}
        />
        <Button
          type="submit"
          variant="secondary"
          icon={MagnifyingGlassIcon}
          isLoading={searchMutation.isLoading}
          disabled={searchMutation.isLoading || subscribeMutation.isLoading || isBatchProcessing}
        >
          Search
        </Button>
      </form>

      {(message || statusMessage) && (
        <div
          className={`mt-3 rounded-md border px-3 py-2 text-sm ${
            message && group
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
          role="status"
        >
          {message || statusMessage}
        </div>
      )}

      {group && (
        <div
          className="mt-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
          data-testid="public-group-search-result"
        >
          <div className="flex items-start gap-3">
            {group.photo ? (
              <img
                src={group.photo}
                alt={`${group.title} photo`}
                className="h-14 w-14 flex-none rounded-full object-cover"
              />
            ) : (
              <div className="flex h-14 w-14 flex-none items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
                <UserGroupIcon className="h-7 w-7" aria-hidden="true" />
              </div>
            )}

            <div className="min-w-0 flex-1">
              <h5 className="truncate text-base font-semibold text-gray-900">
                {group.title}
              </h5>
              <p className="truncate text-sm text-gray-600">
                @{group.username}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {Number(group.member_count || 0).toLocaleString()} members
              </p>
              {group.description && (
                <p className="mt-2 line-clamp-3 text-sm text-gray-600">
                  {group.description}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_auto]">
            <label htmlFor="public-group-project" className="sr-only">
              Project
            </label>
            <select
              id="public-group-project"
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              disabled={projectsQuery.isLoading || subscribeMutation.isLoading || isBatchProcessing}
            >
              <option value="">
                {projectsQuery.isLoading ? 'Loading projects...' : 'Select project'}
              </option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            <Button
              variant="success"
              isLoading={subscribeMutation.isLoading}
              disabled={!projectId || subscribeMutation.isLoading || isBatchProcessing}
              onClick={() => subscribeMutation.mutate()}
            >
              Subscribe &amp; Add
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4 border-t border-indigo-200 pt-4">
        <h5 className="text-sm font-semibold text-gray-900">Subscribe &amp; add multiple</h5>
        <p className="mt-1 text-xs text-gray-600">
          Paste up to {MAX_BATCH_GROUPS} public usernames or links. They are processed one at a
          time and attached to one project without running Basic Info, AI Analysis, or Quality Check.
        </p>

        <form onSubmit={handleBatchSubscribe} className="mt-3 space-y-3">
          <div>
            <label htmlFor="public-group-batch-input" className="block text-xs font-medium text-gray-700">
              Public group usernames or links
            </label>
            <textarea
              id="public-group-batch-input"
              value={batchInput}
              onChange={(event) => setBatchInput(event.target.value)}
              rows={5}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              placeholder={'@first_group\nhttps://t.me/second_group'}
              disabled={
                isBatchProcessing || subscribeMutation.isLoading || searchMutation.isLoading
              }
            />
          </div>

          <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
            <label htmlFor="public-group-batch-project" className="sr-only">
              Project for batch add
            </label>
            <select
              id="public-group-batch-project"
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              disabled={
                projectsQuery.isLoading ||
                isBatchProcessing ||
                subscribeMutation.isLoading ||
                searchMutation.isLoading
              }
            >
              <option value="">
                {projectsQuery.isLoading ? 'Loading projects...' : 'Select project'}
              </option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            <Button
              type="submit"
              variant="success"
              isLoading={isBatchProcessing}
              disabled={
                !projectId ||
                !batchInput.trim() ||
                batchInput.split(/\r?\n/).filter(line => line.trim()).length > MAX_BATCH_GROUPS ||
                isBatchProcessing ||
                subscribeMutation.isLoading ||
                searchMutation.isLoading
              }
            >
              Subscribe &amp; Add All
            </Button>
          </div>

          {batchInput.split(/\r?\n/).filter(line => line.trim()).length > MAX_BATCH_GROUPS && (
            <p className="text-xs text-red-700" role="alert">
              This batch contains more than {MAX_BATCH_GROUPS} non-empty lines. Split it into smaller batches.
            </p>
          )}
        </form>

        {batchResults.length > 0 && (
          <div
            className="mt-4 overflow-hidden rounded-md border border-gray-200 bg-white"
            aria-live="polite"
          >
            <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700">
              Results: {batchResults.length} input lines
            </div>
            <ul className="max-h-72 divide-y divide-gray-100 overflow-y-auto" aria-label="Batch subscription results">
              {batchResults.map(row => {
                const status = BATCH_STATUS[row.status] || BATCH_STATUS.failed;
                return (
                  <li key={row.id} className="px-3 py-2 text-xs">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="min-w-0 break-all font-medium text-gray-900">
                        Line {row.id + 1}: {row.input}
                      </span>
                      <span className={`flex-none rounded-full px-2 py-1 font-medium ${status.classes}`}>
                        {status.label}
                      </span>
                    </div>
                    <p className="mt-1 text-gray-600">{row.message}</p>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

export default PublicGroupSearchCard;
